import React, { useEffect, useState, useMemo } from "react";
import Layout from "../../layout/Main/Layout";
import CustomTable from "../../components/Custom/Table/CustomTable";
import { get, post, put, del } from "../../config/axios";
import {
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toastMessage } from "../../utils/toastMessage";

const DassQuestions = () => {
  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editData, setEditData] = useState({ 
    _id: "",
    question: "", 
    option: "", 
    category: "" 
  });
  const [deleteData, setDeleteData] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [availableOptions, setAvailableOptions] = useState([]);

  const columns = [
    { 
      id: "sno", 
      label: "Sno", 
      minWidth: 70, 
      align: "center",
      format: (value, row, index) => index + 1
    },
    { 
      id: "question", 
      label: "Question", 
      minWidth: 300, 
      align: "left" 
    },
    { 
      id: "category", 
      label: "Category", 
      minWidth: 120, 
      align: "left" 
    },
    { 
      id: "optionViewStyle", 
      label: "Option View Style", 
      minWidth: 150, 
      align: "left" 
    },
    { 
      id: "lastEditDate", 
      label: "Last Edit Date", 
      minWidth: 120, 
      align: "center",
      format: (value) => value ? new Date(value).toLocaleDateString('en-GB') : "–"
    },
    { 
      id: "action", 
      label: "Actions", 
      minWidth: 120, 
      align: "center" 
    },
  ];

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const res = await get("/api/dashboard/cbt/questions/dass");
      console.log("Dass questions response:", res);
      
      if (res?.success && res?.questions) {
        const mappedData = res.questions.map((item, index) => ({
          _id: item._id,
          question: item.question || "",
          category: item.category || "",
          optionViewStyle: item.optionViewStyle || "LIST",
          lastEditDate: item.updatedAt || item.createdAt,
          sno: index + 1,
          action: {
            edit: true,
            delete: true
          }
        }));
        setTableData(mappedData);
        setTotalData(mappedData.length);
      } else {
        setTableData([]);
        setTotalData(0);
      }
    } catch (error) {
      console.error("Error fetching dass questions:", error);
      toastMessage("Failed to fetch dass questions", "error");
      setTableData([]);
      setTotalData(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      const res = await get(`/api/dashboard/cbt/options`);
      const seenTypes = new Set();
      const mappedOptions = [];
  
      res?.questions?.forEach((opt) => {
        if (!seenTypes.has(opt.optionType)) {
          seenTypes.add(opt.optionType);
          mappedOptions.push({
            label: opt.optionType,
            value: opt._id,
          });
        }
      });
  
      setAvailableOptions(mappedOptions);
    } catch (err) {
      console.error("Failed to fetch options:", err);
    }
  };
  
  useEffect(() => {
    fetchQuestions();
    fetchOptions();
  }, []);

  const handleEdit = (row) => {
    setEditData({
      _id: row._id,
      question: row.question,
      option: row.option || "",
      category: row.category
    });
    setEditModal(true);
  };

  const handleDelete = (row) => {
    setDeleteData(row);
    setDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteData) return;
    
    try {
      const response = await del(`/api/dashboard/cbt/questions/dass/${deleteData._id}`);
      
      if (response?.success) {
        toastMessage("Question deleted successfully", "success");
        setDeleteModal(false);
        setDeleteData(null);
        fetchQuestions();
      } else {
        toastMessage(response?.message || "Failed to delete question", "error");
      }
    } catch (error) {
      console.error("Error deleting question:", error);
      toastMessage("Failed to delete question", "error");
    }
  };

  const handleSubmit = async (formData, isEditing = false, id = null) => {
    try {
      setLoading(true);
      const { question, option, category } = formData;
      const payload = {
        question,
        option,
        category,
      };

      if (isEditing) {
        // Update existing question
        const response = await put(`/api/dashboard/cbt/questions/dass/${id}`, payload);
        
        if (response?.success) {
          toastMessage("Question updated successfully", "success");
          setEditModal(false);
          setEditData({ _id: "", question: "", option: "", category: "" });
          fetchQuestions();
        } else {
          toastMessage(response?.message || "Failed to update question", "error");
        }
      } else {
        // Create new question
        const response = await post("/api/dashboard/cbt/questions/dass", payload);
        
        if (response?.success) {
          toastMessage("Question created successfully", "success");
          setIsModalOpen(false);
          setEditData({ _id: "", question: "", option: "", category: "" });
          fetchQuestions();
        } else {
          toastMessage(response?.message || "Failed to create question", "error");
        }
      }
    } catch (error) {
      console.error("Error saving question:", error);
      toastMessage("Failed to save question", "error");
    } finally {
      setLoading(false);
    }
  };

  const openModal = (type, dataForEdit) => {
    if (type === "add") {
      setEditData({ _id: "", question: "", option: "", category: "" });
      setIsModalOpen(true);
    } else if (type === "edit") {
      setEditModal(true);
      setEditData(dataForEdit);
    }
  };

  const closeModal = (type) => {
    if (type === "add") {
      setIsModalOpen(false);
    } else if (type === "edit") {
      setEditModal(false);
      setEditData({ _id: "", question: "", option: "", category: "" });
    }
  };

  const handleChange = (page) => {
    setCurrentPage(page);
    setPage(page);
  };

  const filteredData = useMemo(() => {
    if (!search) return tableData;
    return tableData.filter((item) =>
      item.question.toLowerCase().includes(search.toLowerCase()) ||
      item.category.toLowerCase().includes(search.toLowerCase())
    );
  }, [tableData, search]);

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Dass Questions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage DASS (Depression, Anxiety, Stress Scale) questions
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openModal("add")}
          >
            Add Question
          </Button>
        </Box>

        {/* Questions Table */}
        <CustomTable
          columns={columns}
          data={filteredData}
          loading={loading}
          search={search}
          onSearchChange={setSearch}
          currentPage={currentPage}
          totalData={totalData}
          perPage={perPage}
          onPageChange={handleChange}
          searchPlaceholder="Search questions..."
          handleEdit={handleEdit}
          handleDelete={handleDelete}
        />

        {/* Add Question Modal */}
        <Dialog open={isModalOpen} onClose={() => closeModal("add")} maxWidth="md" fullWidth>
          <DialogTitle>Add New DASS Question</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Question Text"
                value={editData.question}
                onChange={(e) => setEditData({ ...editData, question: e.target.value })}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Option</InputLabel>
                <Select
                  value={editData.option}
                  onChange={(e) => setEditData({ ...editData, option: e.target.value })}
                  label="Select Option"
                >
                  {availableOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Select Category</InputLabel>
                <Select
                  value={editData.category}
                  onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                  label="Select Category"
                >
                  <MenuItem value="stress">Stress</MenuItem>
                  <MenuItem value="anxiety">Anxiety</MenuItem>
                  <MenuItem value="depression">Depression</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => closeModal("add")}>Cancel</Button>
            <Button 
              onClick={() => handleSubmit(editData, false)} 
              variant="contained"
              disabled={!editData.question || !editData.option || !editData.category}
            >
              Add Question
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Question Modal */}
        <Dialog open={editModal} onClose={() => closeModal("edit")} maxWidth="md" fullWidth>
          <DialogTitle>Edit DASS Question</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Question Text"
                value={editData.question}
                onChange={(e) => setEditData({ ...editData, question: e.target.value })}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Option</InputLabel>
                <Select
                  value={editData.option}
                  onChange={(e) => setEditData({ ...editData, option: e.target.value })}
                  label="Select Option"
                >
                  {availableOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Select Category</InputLabel>
                <Select
                  value={editData.category}
                  onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                  label="Select Category"
                >
                  <MenuItem value="stress">Stress</MenuItem>
                  <MenuItem value="anxiety">Anxiety</MenuItem>
                  <MenuItem value="depression">Depression</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => closeModal("edit")}>Cancel</Button>
            <Button 
              onClick={() => handleSubmit(editData, true, editData._id)} 
              variant="contained"
              disabled={!editData.question || !editData.option || !editData.category}
            >
              Update Question
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModal} onClose={() => setDeleteModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Delete DASS Question</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Are you sure you want to delete this question?
            </Typography>
            {deleteData && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  Question:
                </Typography>
                <Typography variant="body2">
                  {deleteData.question}
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                  Category:
                </Typography>
                <Typography variant="body2">
                  {deleteData.category}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteModal(false)}>Cancel</Button>
            <Button 
              onClick={handleDeleteConfirm} 
              variant="contained"
              color="error"
            >
              Delete Question
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default DassQuestions;
